import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';
import QRCodeView from '../../utils/electrical/QRCodeView.jsx';
import { transformForPDF, MAX_ROWS_PER_PAGE } from './transform';

const COLORS = {
  navy: '#0B2545',
  blue: '#1D4E89',
  midGray: '#666666',
  green: '#008855',
  red: '#CC0000',
  amber: '#B8860B'
};

const styles = StyleSheet.create({
  page: {
    orientation: 'landscape',
    fontFamily: 'Helvetica',
    fontSize: 7,
    padding: 20,
    backgroundColor: '#FFFFFF'
  },
  header: {
    backgroundColor: '#0B2545',
    padding: 6,
    flexDirection: 'column'
  },
  title: { fontSize: 13, fontWeight: 'bold', color: '#FFFFFF', textAlign: 'center' },
  subtitle: { fontSize: 8, color: '#D0D8E8', textAlign: 'center' },
  section: {
    marginBottom: 6,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#1D4E89'
  },
  sectionTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#FFFFFF',
    backgroundColor: '#1D4E89',
    padding: 3
  },
  fieldRow: { flexDirection: 'row', marginBottom: 2 },
  fieldLabel: { fontSize: 7, fontWeight: 'bold', width: '18%', color: '#404040' },
  fieldValue: { fontSize: 7, width: '55%' },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000000'
  },
  tableRow: {
    flexDirection: 'row',
    borderStyle: 'solid',
    borderWidth: 0.5,
    borderColor: '#000000'
  },
  tableHeader: { backgroundColor: '#1D4E89', color: '#FFFFFF', fontWeight: 'bold' },
  tableCell: { margin: 1, padding: 2, fontSize: 6 },
  summary: { flexDirection: 'row', fontSize: 8, marginBottom: 4 }
});

const APPLIANCE_COLUMNS = [
  { label: 'Item', width: '6%' },
  { label: 'Description', width: '20%' },
  { label: 'Class', width: '6%' },
  { label: 'Visual', width: '6%' },
  { label: 'Earth Ω', width: '7%' },
  { label: 'Lead Ω', width: '6%' },
  { label: 'IR MΩ', width: '7%' },
  { label: 'Leak mA', width: '7%' },
  { label: 'Result', width: '9%' },
  { label: 'Retest', width: '6%' },
  { label: 'QR', width: '10%' },
  { label: 'Notes', width: '16%' }
];

const ApplianceRow = ({ appliance, rowColor }) => (
  <View style={[styles.tableRow, { backgroundColor: rowColor }]}>
    <View style={{ width: '6%' }}><Text style={styles.tableCell}>{appliance.itemId}</Text></View>
    <View style={{ width: '20%' }}><Text style={styles.tableCell}>{appliance.description}</Text></View>
    <View style={{ width: '6%' }}><Text style={styles.tableCell}>{appliance.applianceClass}</Text></View>
    <View style={{ width: '6%' }}><Text style={styles.tableCell}>{appliance.visualInspection}</Text></View>
    <View style={{ width: '7%' }}><Text style={styles.tableCell}>{appliance.earthContinuity}</Text></View>
    <View style={{ width: '6%' }}><Text style={styles.tableCell}>{appliance.leadResistance}</Text></View>
    <View style={{ width: '7%' }}><Text style={styles.tableCell}>{appliance.insulationResistance}</Text></View>
    <View style={{ width: '7%' }}><Text style={styles.tableCell}>{appliance.touchLeakage}</Text></View>
    <View style={{ width: '9%' }}>
      <Text style={[styles.tableCell, { color: appliance.autoResult === 'FAIL' ? COLORS.red : COLORS.green, fontWeight: 'bold' }]}>
        {appliance.testResult}
      </Text>
    </View>
    <View style={{ width: '6%' }}><Text style={styles.tableCell}>{appliance.retestPeriod}</Text></View>
    <View style={{ width: '10%' }}>
      <QRCodeView payload={`PAT-${appliance.itemId}-${appliance.description}`} moduleSize={1} quietZone={1} />
    </View>
    <View style={{ width: '16%' }}><Text style={styles.tableCell}>{appliance.notes}</Text></View>
  </View>
);

const PATPDF = ({ formData }) => {
  const data = transformForPDF(formData);
  const s = data.summary;

  // Split appliances into paginated chunks (max 30 per page)
  const chunks = [];
  for (let i = 0; i < data.appliances.length; i += MAX_ROWS_PER_PAGE) {
    chunks.push(data.appliances.slice(i, i + MAX_ROWS_PER_PAGE));
  }
  const pageCount = chunks.length > 0 ? chunks.length : 1;

  return (
    <Document>
      {Array.from({ length: pageCount }, (_, pageIdx) => (
        <Page key={pageIdx} size="A4" style={styles.page}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{data.title}</Text>
            <Text style={styles.subtitle}>{data.standard}</Text>
          </View>

          {/* Job details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Test Details</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Register No:</Text>
              <Text style={styles.fieldValue}>
                {data.jobDetails.certificateNumber} | {data.jobDetails.testDate}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Duty Holder:</Text>
              <Text style={styles.fieldValue}>{data.jobDetails.dutyHolder}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Location:</Text>
              <Text style={styles.fieldValue}>{data.jobDetails.testLocation}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engineer:</Text>
              <Text style={styles.fieldValue}>
                {data.jobDetails.engineerName} (Card: {data.jobDetails.engineerCardNumber})
              </Text>
            </View>
          </View>

          {/* Appliance register */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Appliance Test Register - Page {pageIdx + 1} of {pageCount}
              {chunks.length > 0 && ` | ${s.totalCount} items | PASS ${s.passCount} | FAIL ${s.failCount}`}
            </Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                {APPLIANCE_COLUMNS.map(col => (
                  <View key={col.label} style={{ width: col.width }}>
                    <Text style={styles.tableCell}>{col.label}</Text>
                  </View>
                ))}
              </View>
              {(chunks[pageIdx] || []).map((a, i) => (
                <ApplianceRow key={i} appliance={a} rowColor={i % 2 === 0 ? undefined : '#F0F0F0'} />
              ))}
            </View>
          </View>

          {/* Statement + summary */}
          <View style={{ marginTop: 4, padding: 6, borderWidth: 1, borderColor: COLORS.blue }}>
            <Text style={{ fontSize: 8 }}>
              {data.registerPasses ? '✓ ' : '✗ '}{data.statement}
            </Text>
            <Text style={{ fontSize: 7, marginTop: 2 }}>
              Summary: {s.totalCount} items tested | {s.passCount} PASS | {s.failCount} FAIL | {s.notTestedCount} NOT TESTED
            </Text>
          </View>

          {/* Declaration */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Declaration</Text>
            <View style={{ flexDirection: 'row', marginTop: 4, justifyContent: 'space-between' }}>
              <View style={styles.signatureBox}>
                <Text>Engineer: {data.declaration.engineerSignature}</Text>
              </View>
              <View style={styles.signatureBox}>
                <Text>Duty Holder: {data.declaration.customerSignature}</Text>
              </View>
            </View>
          </View>

          {/* Footer */}
          <View style={{ position: 'absolute', fontSize: 6, color: '#666666', left: 0, right: 0, bottom: 8, textAlign: 'center' }}>
            <Text>
              {data.jobDetails.certificateNumber} | {data.standard} | Page {pageIdx + 1} of {pageCount}
            </Text>
          </View>
        </Page>
      ))}
    </Document>
  );
};

styles.signatureBox = {
  width: '45%',
  borderBottomWidth: 1,
  borderBottomColor: '#000000',
  borderBottomStyle: 'solid',
  paddingBottom: 6,
  textAlign: 'center',
  fontSize: 7
};

export default PATPDF;